import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {TeamModel} from '../../../models/api/teamModel';
import {
  ApiTeamPreferencesBusinessHours,
  TeamPreferencesBusinessHours,
} from '../../../models/api/teamPreferencesModel';
import {putApiAsync} from '../../../services/http/http';
import {ThunkAsyncAction} from '../../../store/appThunk';
import {getCompany} from '../company/companySelectors';

/*
 * Action types.
 */

export enum TeamPreferencesActionsEnum {
  TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED = '@@data/teamPreferences/TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED',
  TEAM_BUSINESS_HOURS_PREFERENCES_UPDATE_FAILED = '@@data/teamPreferences/TEAM_BUSINESS_HOURS_PREFERENCES_UPDATE_FAILED',
  TEAM_BUSINESS_HOURS_PREFERENCES_RESET_ERROR = '@@data/teamPreferences/TEAM_BUSINESS_HOURS_PREFERENCES_RESET_ERROR',
}

/*
 * Types.
 */

export type TeamBusinessHoursPreferencesUpdatedPayload =
  | {teamId: TeamModel['id']; businessHours: TeamPreferencesBusinessHours}
  | {teamId: TeamModel['id']; businessHours: {enabled: false}};
type TeamBusinessHoursPreferencesUpdateFailedPayload = TeamBusinessHoursPreferencesUpdatedPayload;
type TeamBusinessHoursPreferencesUpdateResetErrorPayload = {teamId: TeamModel['id']};

export interface TeamBusinessHoursPreferencesUpdatedAction
  extends ActionWithPayload<TeamBusinessHoursPreferencesUpdatedPayload> {
  type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED;
}

export interface TeamBusinessHoursPreferencesUpdateFailedAction
  extends ActionWithPayload<TeamBusinessHoursPreferencesUpdateFailedPayload> {
  type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATE_FAILED;
}

export interface TeamBusinessHoursPreferencesResetErrorAction
  extends ActionWithPayload<TeamBusinessHoursPreferencesUpdateResetErrorPayload> {
  type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_RESET_ERROR;
}

/*
 * Action creators.
 */

export function updateTeamBusinessHours(
  businessHoursPreferences: TeamBusinessHoursPreferencesUpdatedPayload,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const body = {...businessHoursPreferences.businessHours};

    const resetErrorAction: TeamBusinessHoursPreferencesResetErrorAction = {
      type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_RESET_ERROR,
      payload: {teamId: businessHoursPreferences.teamId},
    };
    dispatch(resetErrorAction);

    try {
      const result = await putApiAsync(
        ApiTeamPreferencesBusinessHours,
        `${company.url}/teams/${businessHoursPreferences.teamId}/business_hours`,
        body,
      );
      const action: TeamBusinessHoursPreferencesUpdatedAction = {
        type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED,
        payload: {teamId: businessHoursPreferences.teamId, businessHours: result},
      };
      dispatch(action);
    } catch (e) {
      const action: TeamBusinessHoursPreferencesUpdateFailedAction = {
        type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATE_FAILED,
        payload: businessHoursPreferences,
      };
      dispatch(action);
    }
  };
}

/*
 * Union type.
 */

export type TeamPreferencesAction =
  | TeamBusinessHoursPreferencesUpdatedAction
  | TeamBusinessHoursPreferencesUpdateFailedAction
  | TeamBusinessHoursPreferencesResetErrorAction;
